import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../lib/language'

export default function LandingPage() {
  const { t, language } = useLanguage()
  const [scrolled, setScrolled] = useState(false)
  const [showTop, setShowTop] = useState(false)

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60)
      setShowTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500&display=swap');

    * { box-sizing: border-box; margin: 0; padding: 0; }

    /* \u2500\u2500 ADA \u2500\u2500 */
    .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
    .lca-skip { position:absolute; top:-100px; left:16px; background:#C9A84C; color:#0D1B3E; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
    .lca-skip:focus { top:16px; }
    :focus-visible { outline:2px solid #C9A84C; outline-offset:3px; border-radius:4px; }

    /* \u2500\u2500 NAV \u2500\u2500 */
    .lca-nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      padding: 18px 48px;
      display: flex; align-items: center; justify-content: space-between;
      background: linear-gradient(to bottom, rgba(0,0,0,0.7), transparent);
      transition: background 0.4s;
    }
    .lca-nav.scrolled { background: rgba(13,27,62,0.97); backdrop-filter: blur(8px); }
    .lca-nav-brand { display: flex; flex-direction: column; text-decoration: none; flex-shrink: 0; }
    .lca-nav-name {
      font-family: 'Cormorant Garamond', serif;
      font-size: 22px; font-weight: 600;
      color: #C9A84C; letter-spacing: 0.02em; line-height: 1.2;
    }
    .lca-nav-sub { font-size: 8px; color: rgba(201,168,76,0.85); letter-spacing: 0.15em; margin-top: 3px; }
    .lca-nav-links { display: flex; gap: 28px; align-items: center; flex-shrink: 0; }
    .lca-nav-link { color: rgba(255,255,255,0.95); font-size: 12px; letter-spacing: 0.05em; text-decoration: none; transition: color 0.2s; white-space: nowrap; }
    .lca-nav-link:hover { color: #C9A84C; }
    .lca-nav-signin { color: #C9A84C; font-size: 12px; font-weight: 500; background: transparent; padding: 0; border: none; cursor: pointer; letter-spacing: 0.08em; text-decoration: none; display: inline-block; transition: opacity 0.2s; white-space: nowrap; }
    .lca-nav-signin:hover { opacity: 0.7; }

    @media(max-width:900px){
      .lca-nav { padding: 14px 24px; }
      .lca-nav-links { gap: 18px; }
      .lca-nav-name { font-size: 18px; }
    }
    @media(max-width:768px){
      .lca-nav { padding: 12px 20px; }
      .lca-nav-name { font-size: 15px; }
      .lca-nav-sub { display: none; }
      .lca-nav-links { gap: 12px; }
      .lca-nav-link { font-size: 11px; }
      .lca-nav-signin { font-size: 11px; }
    }
    @media(max-width:580px){
      .lca-nav-name { font-size: 13px; }
      .lca-nav-links .lca-nav-link { display: none; }
      .lca-nav-links { gap: 10px; }
    }

    /* \u2500\u2500 HERO \u2500\u2500 */
    .lca-hero { position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center; text-align: center; overflow: hidden; }
    .lca-hero-img { position: absolute; inset: 0; background-image: url('/lca_hero.jpg'); background-size: cover; background-position: center; }
    .lca-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(6,14,32,0.80) 0%, rgba(6,14,32,0.50) 45%, rgba(6,14,32,0.88) 100%); }
    .lca-hero-content { position: relative; z-index: 2; padding: 120px 32px 80px; max-width: 860px; margin: 0 auto; }

    /* \u2705 BIGGER logo ring */
    .lca-hero-logo-ring { width: 260px; height: 260px; border-radius: 50%; border: 1.5px solid rgba(201,168,76,0.55); display: flex; align-items: center; justify-content: center; margin: 0 auto 32px; box-shadow: 0 4px 24px rgba(201,168,76,0.25), 0 0 0 8px rgba(13,27,62,0.4); }
    .lca-hero-logo-inner { width: 220px; height: 220px; border-radius: 50%; border: 1px solid rgba(201,168,76,0.35); display: flex; align-items: center; justify-content: center; }
    .lca-hero-logo-inner img { width: 200px; height: 200px; object-fit: contain; }

    .lca-hero-rule { width: 40px; height: 1px; background: #C9A84C; margin: 0 auto 22px; opacity: 0.8; }
    .lca-hero h1 { font-family: 'Cormorant Garamond', serif; font-size: 36px; font-weight: 400; color: #fff; line-height: 1.4; margin-bottom: 18px; text-wrap: balance; letter-spacing: 0.01em; }
    .lca-gold { color: #D4B05C; }
    .lca-hero-sub { font-size: 14px; color: rgba(255,255,255,0.82); line-height: 1.9; max-width: 540px; margin: 0 auto 36px; }
    .lca-hero-h1-clean { font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif; font-size: clamp(26px, 4vw, 44px); font-weight: 500; line-height: 1.3; letter-spacing: -0.01em; color: #fff; margin: 0; max-width: 900px; }
    .lca-hero-h1-clean .lca-gold { color: #C9A84C; font-style: italic; }
    .lca-hero-dot { color: #C9A84C; margin: 0 14px; font-style: normal; opacity: 0.85; }
    .lca-hero-tagline-sub { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: clamp(15px, 2vw, 19px); color: rgba(255,255,255,0.88); margin: 16px auto 24px; max-width: 640px; letter-spacing: 0.01em; }
    .lca-hero-seg { white-space: nowrap; }
    @media (max-width: 640px) {
      .lca-hero-dot { display: none; }
      .lca-hero-seg { display: block; line-height: 1.32; }
      .lca-hero-tagline-sub { padding: 0 18px; }
    }
    .lca-hero-cta-group { display: flex; flex-wrap: wrap; justify-content: center; gap: 14px; margin-top: 24px; max-width: 900px; width: 100%; }
    .lca-hero-cta { display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 200px; padding: 16px 28px; background: linear-gradient(135deg, #C9A84C 0%, #B8941F 100%); color: #0D1B3E; text-decoration: none; border-radius: 10px; box-shadow: 0 4px 12px rgba(13,27,62,0.35), 0 1px 0 rgba(255,255,255,0.15) inset; border: 1px solid rgba(201,168,76,0.6); transition: transform 0.15s, box-shadow 0.2s; cursor: pointer; }
    .lca-hero-cta:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(13,27,62,0.45), 0 1px 0 rgba(255,255,255,0.2) inset; }
    .lca-hero-cta:focus-visible { outline: 3px solid #fff; outline-offset: 3px; }
    .lca-hero-cta-label { font-family: 'Inter', -apple-system, sans-serif; font-size: 15px; font-weight: 700; letter-spacing: 0.03em; color: #0D1B3E; }
    .lca-hero-cta-sub { font-size: 11px; font-weight: 600; color: #0D1B3E; letter-spacing: 0.08em; text-transform: uppercase; margin-top: 4px; }
    .lca-hero-btn { background: #C9A84C; color: #0D1B3E; padding: 15px 40px; border-radius: 4px; font-size: 13px; font-weight: 500; border: none; cursor: pointer; letter-spacing: 0.07em; box-shadow: 0 3px 0 rgba(0,0,0,0.25); transition: opacity 0.2s, transform 0.15s; display: inline-block; }
    .lca-hero-btn:hover { opacity: 0.9; transform: translateY(-2px); }
    @media (max-width: 768px) {
      .lca-hero-cta-group { flex-direction: column; align-items: stretch; max-width: 320px; }
      .lca-hero-cta { width: 100%; }
    }
    @media(max-width:640px){
      .lca-hero h1 { font-size: 24px; line-height: 1.45; }
      .lca-hero-sub { font-size: 13px; }
      .lca-hero-logo-ring { width: 180px; height: 180px; }
      .lca-hero-logo-inner { width: 152px; height: 152px; }
      .lca-hero-logo-inner img { width: 130px; height: 130px; }
      .lca-hero-content { padding: 100px 20px 60px; }
    }
    @media(max-width:380px){
      .lca-hero h1 { font-size: 20px; line-height: 1.4; }
      .lca-hero-logo-ring { width: 140px; height: 140px; }
      .lca-hero-logo-inner { width: 110px; height: 110px; }
      .lca-hero-logo-inner img { width: 95px; height: 95px; }
    }

    /* \u2500\u2500 TRUST \u2500\u2500 */
    .lca-trust { background: #0D1B3E; padding: 18px 32px; display: flex; justify-content: center; gap: 28px; flex-wrap: wrap; border-bottom: 1px solid rgba(201,168,76,0.15); }
    .lca-trust-item { display: flex; align-items: center; gap: 8px; font-size: 11px; color: rgba(255,255,255,0.85); letter-spacing: 0.03em; }
    .lca-trust-dot { width: 4px; height: 4px; border-radius: 50%; background: #C9A84C; flex-shrink: 0; }
    @media(max-width:640px){ .lca-trust { gap: 12px 18px; padding: 14px 16px; } .lca-trust-item { font-size: 10px; } }

    /* \u2500\u2500 PORTALS \u2500\u2500 */
    .lca-portals { background: #F8F7F4; padding: 80px 48px; }
    .lca-portals-h2 { font-family: 'Cormorant Garamond', serif; font-size: 34px; font-weight: 400; color: #0D1B3E; text-align: center; margin-bottom: 10px; }
    .lca-portals-sub { font-size: 14px; color: #5A5850; text-align: center; line-height: 1.75; max-width: 380px; margin: 0 auto 48px; }
    .lca-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; max-width: 860px; margin: 0 auto; border-radius: 10px; overflow: hidden; border: 1px solid #0D1B3E; }
    .lca-card { background: #0D1B3E; padding: 36px 28px; border-right: 1px solid rgba(255,255,255,0.07); transition: background 0.3s; cursor: pointer; text-align: center; }
    .lca-card:last-child { border-right: none; }
    .lca-card:hover { background: #fff; }
    .lca-card:hover .lca-card-title { color: #0D1B3E; }
    .lca-card:hover .lca-card-desc { color: #6A6860; }
    .lca-card:hover .lca-card-btn { background: linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%); color: #fff; border-color: transparent; box-shadow: 0 4px 14px rgba(13,27,62,0.3); }
    .lca-card-eyebrow { font-size: 10px; letter-spacing: 0.2em; color: #C9A84C; margin-bottom: 14px; font-weight: 500; }
    .lca-card-rule { width: 22px; height: 1px; background: #C9A84C; margin: 0 auto 16px; opacity: 0.55; }
    .lca-card-title { font-family: 'Cormorant Garamond', serif; font-size: 20px; font-weight: 500; color: #fff; margin-bottom: 12px; transition: color 0.3s; line-height: 1.2; }
    .lca-card-desc { font-size: 12px; color: rgba(255,255,255,0.78); line-height: 1.85; margin-bottom: 28px; transition: color 0.3s; }
    /* \u2705 CTA PASS \u2014 portal card buttons: navy default, gold gradient hover */
    .lca-card-btn { display: inline-block; padding: 9px 22px; font-size: 11px; font-weight: 600; letter-spacing: 0.07em; cursor: pointer; border-radius: 10px; background: #0D1B3E; color: #C9A84C; border: 1px solid rgba(201,168,76,0.45); box-shadow: 0 2px 8px rgba(13,27,62,0.25); transition: all 0.25s; text-decoration: none; }
    .lca-card-btn:hover { background: linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%); color: #fff; border-color: transparent; transform: translateY(-2px); box-shadow: 0 4px 14px rgba(13,27,62,0.35); }
    .lca-card-btn:focus-visible { outline: 3px solid #C9A84C; outline-offset: 3px; }
    .lca-signin-note { text-align: center; margin-top: 28px; font-size: 13px; color: #6A6860; }
    .lca-signin-note a { color: #7A5E18; text-decoration: none; font-weight: 500; }
    .lca-signin-note a:hover { text-decoration: underline; }
    @media(max-width:768px){ .lca-cards { grid-template-columns: 1fr; } .lca-card { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.07); } .lca-card:last-child { border-bottom: none; } .lca-portals { padding: 56px 20px; } .lca-portals-h2 { font-size: 26px; } }

    /* \u2500\u2500 BACK TO TOP \u2500\u2500 */
    .lca-back-top-wrap { background: #0D1B3E; display: flex; justify-content: center; padding: 16px 0 0; }
    .lca-back-top { position:fixed; bottom:24px; right:20px; width:44px; height:44px; border-radius:50%; background:#C9A84C; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:18px; color:#0D1B3E; font-weight:700; box-shadow:0 4px 16px rgba(0,0,0,0.3); transition:all 0.3s; opacity:0; pointer-events:none; z-index:150; }
    .lca-back-top.visible { opacity:1; pointer-events:auto; }
    .lca-back-top:hover { transform:translateY(-4px); box-shadow:0 6px 20px rgba(0,0,0,0.4); }

    /* \u2500\u2500 FOOTER \u2500\u2500 */
    .lca-footer { background: #0D1B3E; border-top: 1px solid rgba(201,168,76,0.15); padding: 32px 48px 28px; text-align: center; }
    .lca-footer-logo { display: inline-flex; align-items: center; gap: 12px; text-decoration: none; margin-bottom: 24px; }
    .lca-footer-logo-circle { width: 48px; height: 48px; border-radius: 50%; border: 1px solid rgba(201,168,76,0.3); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .lca-footer-logo-circle img { width: 44px; height: 44px; object-fit: contain; }
    .lca-footer-brand-name { font-family: 'Cormorant Garamond', serif; font-size: 15px; color: rgba(255,255,255,0.82); letter-spacing: 0.04em; display: block; text-align: left; }
    .lca-footer-brand-sub { font-size: 8px; color: #C9A84C; letter-spacing: 0.12em; margin-top: 2px; display: block; text-align: left; }
    .lca-footer-links { display: flex; gap: 24px; justify-content: center; flex-wrap: wrap; margin-bottom: 24px; }
    .lca-footer-links a { font-size: 11px; color: rgba(255,255,255,0.78); text-decoration: none; letter-spacing: 0.04em; transition: color 0.2s; }
    .lca-footer-links a:hover { color: #C9A84C; }
    .lca-footer-bottom { border-top: 1px solid rgba(255,255,255,0.06); padding-top: 18px; }
    .lca-footer-copy { font-size: 10px; color: rgba(255,255,255,0.65); margin-bottom: 6px; }
    .lca-footer-veteran { font-size: 11px; color: #C9A84C; letter-spacing: 0.05em; }
    @media(max-width:520px){ .lca-footer { padding: 32px 20px 20px; } .lca-footer-links { gap: 14px; } }
  `

  return (
    <div style={{ background: '#0D1B3E', minHeight: '100vh' }}>
      <style>{styles}</style>

      <a href="#lca-main" className="lca-skip">Skip to main content</a>

      {/* NAV */}
      <nav className={`lca-nav${scrolled ? ' scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <Link to="/" className="lca-nav-brand" aria-label="Loan Closing Agent LLC homepage">
          <span className="lca-nav-name">Loan Closing Agent LLC</span>
        </Link>
        <div className="lca-nav-links">
          <Link to="/services" className="lca-nav-link">{t('nav_services')}</Link>
          <Link to="/about" className="lca-nav-link">{t('nav_about')}</Link>
          <Link to="/contact" className="lca-nav-link">{t('nav_contact')}</Link>
          <Link to="/login" className="lca-nav-signin" aria-label="Sign in to your existing account.">
            {t('nav_signin')}
          </Link>
        </div>
      </nav>

      {/* MAIN */}
      <main id="lca-main" tabIndex={-1}>

        {/* HERO */}
        <section className="lca-hero" aria-labelledby="hero-heading">
          <div className="lca-hero-img" aria-hidden="true" />
          <div className="lca-hero-overlay" aria-hidden="true" />
          <div className="lca-hero-content">
            <div className="lca-hero-logo-ring" aria-hidden="true">
              <div className="lca-hero-logo-inner">
                <img
                  src="/lca_logo_quill_map.png"
                  alt="Loan Closing Agent LLC official logo"
                  onError={e => { e.target.style.display = 'none' }}
                />
              </div>
            </div>
            <div className="lca-hero-rule" aria-hidden="true" />
            <h1 id="hero-heading" className="lca-hero-h1-clean">
              <span className="lca-hero-seg">{t('hero_h1_line1')}</span>
              <span className="lca-hero-dot" aria-hidden="true">·</span>
              <span className="lca-hero-seg">{t('hero_h1_line2')}</span>
              <span className="lca-hero-dot" aria-hidden="true">·</span>
              <span className="lca-hero-seg lca-gold">{t('hero_h1_line3')}</span>
            </h1>
            <p className="lca-hero-tagline-sub">{t('hero_tagline_sub')}</p>
            <div className="lca-hero-rule" aria-hidden="true" />
            <div className="lca-hero-cta-group" role="group" aria-label="Choose your path">
              <Link
                to="/services"
                className="lca-hero-cta lca-hero-cta-primary"
                aria-label="Request a Notary for individuals — personal documents"
              >
                <span className="lca-hero-cta-label">{t('hero_cta_individuals')}</span>
                <span className="lca-hero-cta-sub">{t('hero_cta_individuals_sub')}</span>
              </Link>
              <Link
                to="/register?role=client"
                className="lca-hero-cta lca-hero-cta-primary"
                aria-label="Order a Signing for companies — title or escrow firms"
              >
                <span className="lca-hero-cta-label">{t('hero_cta_companies')}</span>
                <span className="lca-hero-cta-sub">{t('hero_cta_companies_sub')}</span>
              </Link>
              <Link
                to="/register?role=notary"
                className="lca-hero-cta lca-hero-cta-primary"
                aria-label="Join Our Network for notaries — signing agent network"
              >
                <span className="lca-hero-cta-label">{t('hero_cta_notaries')}</span>
                <span className="lca-hero-cta-sub">{t('hero_cta_notaries_sub')}</span>
              </Link>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <div className="lca-trust" role="region" aria-label="Our credentials">
          {['trust_1','trust_2','trust_3','trust_4','trust_5'].map(key => (
            <div key={key} className="lca-trust-item">
              <div className="lca-trust-dot" aria-hidden="true" />
              <span>{t(key)}</span>
            </div>
          ))}
        </div>

        {/* PORTALS */}
        <section id="portals" className="lca-portals" aria-labelledby="portals-heading">
          <h2 id="portals-heading" className="lca-portals-h2">{t('portal_heading')}</h2>
          <p className="lca-portals-sub">
            {t('portal_sub_1')}<br />{t('portal_sub_2')}
          </p>
          <div className="lca-cards">
            <div className="lca-card">
              <div className="lca-card-eyebrow">{t('portal_co_eyebrow')}</div>
              <div className="lca-card-rule" aria-hidden="true" />
              <div className="lca-card-title">{t('portal_co_title')}</div>
              <div className="lca-card-desc">{t('portal_co_desc')}</div>
              <Link to="/register?role=client" className="lca-card-btn" aria-label="Get started as a title or escrow company">
                {t('portal_co_cta')}
              </Link>
            </div>
            <div className="lca-card">
              <div className="lca-card-eyebrow">INDIVIDUALS</div>
              <div className="lca-card-rule" aria-hidden="true" />
              <div className="lca-card-title">Personal Notarizations</div>
              <div className="lca-card-desc">Need a notary for personal documents? We make it simple and stress-free — and we come to you.</div>
              <Link to="/services" className="lca-card-btn" aria-label="Request a notary for your personal documents">
                Request a Notary
              </Link>
            </div>
            <div className="lca-card">
              <div className="lca-card-eyebrow">{t('portal_not_eyebrow')}</div>
              <div className="lca-card-rule" aria-hidden="true" />
              <div className="lca-card-title">{t('portal_not_title')}</div>
              <div className="lca-card-desc">{t('portal_not_desc')}</div>
              <Link to="/register?role=notary" className="lca-card-btn" aria-label="Apply as notary to join our nationwide network">
                {t('portal_not_cta')}
              </Link>
            </div>
          </div>
          <div className="lca-signin-note">
            {t('portal_signin_hint')}{' '}
            <Link to="/login">{t('portal_signin_link')}</Link>
          </div>
        </section>

      </main>

      {/* BACK TO TOP */}
      <button
        className={`lca-back-top${showTop ? ' visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll back to top"
        aria-hidden={!showTop}
        tabIndex={showTop ? 0 : -1}
      >
        ↑
      </button>

      {/* FOOTER */}
      <footer className="lca-footer" role="contentinfo">
        <Link to="/" className="lca-footer-logo" aria-label="Loan Closing Agent LLC, DBA Nationwide Notary Professionals Network — homepage">
          <div className="lca-footer-logo-circle">
            <img src="/lca_logo_quill_map.png" alt="Loan Closing Agent LLC logo" onError={e => { e.target.style.display = 'none' }} />
          </div>
          <div>
            <span className="lca-footer-brand-name">Loan Closing Agent LLC</span>
            <span className="lca-footer-brand-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </div>
        </Link>
        <nav className="lca-footer-links" aria-label="Footer navigation">
          <Link to="/services">{t('footer_services')}</Link>
          <Link to="/about">{t('footer_about')}</Link>
          <Link to="/contact">{t('footer_contact')}</Link>
          <Link to="/accessibility">{t('footer_accessibility')}</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/login">{t('footer_signin')}</Link>
        </nav>
        <div className="lca-footer-bottom">
          <div className="lca-footer-copy">{t('footer_copy')}</div>
          <div className="lca-footer-veteran">{t('footer_veteran')}</div>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.6)', marginTop:6, fontStyle:'italic' }}>
            Loan Closing Agent, LLC is not a law firm and does not provide legal advice.
          </div>
        </div>
      </footer>

    </div>
  )
}
